import { useEffect, useState } from 'react';
import { useCallback } from 'react';

import { BackLink } from '@/components/BackLink';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { Spinner } from '@/components/Spinner';
import { StatusBadge } from '@/components/StatusBadge';
import { usePayCardOnFile } from '@/features/paycard/hooks/usePayCardOnFile';
import type { TranslationKey } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import type { Screen } from '@/lib/kioskSession';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { fetchArrivalMenuState } from '@/services/arrivalMenuState';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';
import { useSelectArrival } from '@/shared/hooks/useSelectArrival';

// The idle rule is KID-1's and this screen re-applies it one screen later. § 4 puts a hook with two
// consumers in `src/shared/hooks/`; the move is deliberately NOT made here because it is a file rename in a
// tree several stories are being built against at once, and a rename is the one conflict a rebase cannot
// resolve. Promoting it is a follow-up of its own, reported with this change.
import { composeArrivalMenu } from '../composeArrivalMenu';
import type { ArrivalIcon, ArrivalMenuState, ArrivalReason } from '../types';
import { ARRIVAL_ICON, ARRIVAL_MENU_UNREAD, ARRIVAL_REASON, queuesForReason } from '../types';
import { ArrivalOptionButton } from './ArrivalOptionButton';

interface ArrivalOption {
  readonly reason: ArrivalReason;
  readonly titleKey: TranslationKey;
  readonly subtitleKey: TranslationKey;
  /**
   * The glyph the frame draws in this option's circle — read from the option row's `icon` instance, not
   * chosen here. Two options share one: the frame gives the file-with-a-folded-corner to both
   * `reconfirmAssignment` and `onboardingPaperwork`.
   */
  readonly icon: ArrivalIcon;
  /**
   * Whether this option is drawn in the tinted "primary" treatment.
   *
   * **A property of the OPTION, and both sources say so independently.** Figma fills
   * `Search for a job` and the hidden `Reconfirm my assignment information` with `#f3feff` and gives them a
   * solid `#00808e` disc, and draws every other row on white with a tinted disc. The prototype's hub marks
   * exactly the same two — `hubSearchJob` and `hubSeeInfo` carry `bg-brand-50` with a `bg-brand` disc,
   * the other five `bg-white` with a `bg-brand-50` disc.
   *
   * ⚠️ **This was `index === 0` until 2026-09-10, which is a different rule that happens to agree in two
   * states out of four.** On a pending submission the filter drops `reconfirmAssignment`, so index 0 became
   * `See what I applied for` and it rendered tinted — and the prototype shows that exact state with
   * `hubAppStatus` on WHITE and `hubSearchJob` tinted below it. Position was never the rule; these two
   * options are simply the two that can lead a list, which is why the position rule looked right.
   */
  readonly recommended?: boolean;
  /** Where the selection takes the candidate. See `DESTINATION_OWED` for the three that are approximated. */
  readonly destination: Screen;
}

/**
 * The option set, in the frame's order.
 *
 * **The order is the frame's and it is a rule — decision 0205 (`in-review`, Operator 2026-09-11).** The
 * sequence below is read from `12 · What brings you in today? — iPad` (`12195:11555`), whose `Options`
 * container is `layoutMode: VERTICAL`, so child order IS render order. The Operator ruled on the live
 * build: *«No estas respetando el orden de what brings you in today declarado en figma»*. This is 0155
 * applied to order — Figma governs how a screen LOOKS, and the sequence of a list is how it looks. It
 * amends 0198's *"no fixed order"* clause and **nothing else of 0198**: its no-count ruling stands below.
 *
 * A row the record removes drops out and the survivors keep their relative order — 0205 § *What this
 * settles*, derived there from 0205 (the order is fixed) plus 0198 (the count is not). The row the frame
 * does NOT draw — `reconfirmAssignment` — leads the menu, on the Operator's ruling of 2026-09-11; see
 * that entry's own note for why a hidden layer at position 2 was never evidence against it.
 *
 * **The array is a constant; the RENDERED list is not.** `options` below filters two entries against the
 * candidate's record, so two people with different outstanding work do NOT see the same menu. KID-5's
 * STATIC guard and decision 0069 (*"Static."*) forbid exactly that, and decision 0162 (approved
 * 2026-09-03) sanctions it — both are live in the Ground Truth and they contradict each other. The build
 * follows 0162, the later approved decision; the contradiction is raised, not resolved here.
 *
 * The leak argument the STATIC guard rests on still stands and is worth keeping in view: offering someone
 * exactly what they owe announces it to a lobby that can read the screen.
 *
 * **There is no count — decision 0198 (approved 2026-09-10), which supersedes 0127's six and 0069's five.**
 * *"There are buttons that do not exist in some use cases"* [Operator]: seven is the size of the DECLARED
 * set, and what a candidate sees is that set minus whatever their record removes. 0198 also forbids
 * asserting the list's length anywhere. See `../types.ts` for why the Figma frame draws fewer and why that
 * is a design item rather than a capability being deleted.
 *
 * ⚠️ **`DESTINATION_OWED` — ONE of these still points at a screen that does not exist.** The
 * see-a-representative off-ramp [KVE-1] has no key in `SCREEN`, so `somethingElse` lands on `finish` — the
 * only terminal "here is what happens next" screen the flow has. That keeps BR-K7's *no dead ends* rule
 * true and is deliberately visible here rather than hidden at the call site; it is an approximation
 * awaiting a decision, not a routing claim.
 *
 * The drug test left that list on 2026-09-11 [defect D1]: `13 · Drug test info` is drawn at all three
 * widths and is built, so `drugTest` → `drugTest` is a real destination [decision 0129 — a destination,
 * never a step]. It used to land on `finish`, whose no-submission branch says *"Please see a
 * representative"* and offers one *Start over*, which is not what a candidate who tapped *Complete my drug
 * test* came for.
 *
 * `apply` → `jobs`, `reconfirm` → `associateStatus` and — since KAP-6 shipped —
 * `onboardingPaperwork` → `i9` are all decided.
 */
const OPTIONS: readonly ArrivalOption[] = [
  {
    /*
     * **FIRST, and it is the Operator's own ruling — asked directly on 2026-09-11: *«1-al principio,
     * primero.»*** It restores [0198](07-decision-log)'s reading in his earlier words: *«Si tengo un
     * placement el de "Reconfirm my assignment information" se muestra primero.»* — with an active
     * placement, this row leads the menu.
     *
     * ⚠️ **The frame does NOT say otherwise, and reading it as if it did is what put this row second.**
     * `12 · What brings you in today? — iPad` holds a layer at position 2 — and it is `visible: false`,
     * turned OFF, so it is not drawn at all. A hidden layer states that the row is CONDITIONAL [0175];
     * it states nothing about where the row goes when the condition is met, because in the frame's own
     * rendering it never goes anywhere. 0205 makes the frame normative for the order of the rows it
     * DRAWS, which is the six below.
     *
     * So there is no conflict left to resolve and no provisional position: the six drawn rows keep the
     * frame's sequence, and the row the frame does not draw is placed by the decision that covers it.
     */
    reason: ARRIVAL_REASON.reconfirmAssignment,
    titleKey: 'arrival.reconfirmAssignment.title',
    subtitleKey: 'arrival.reconfirmAssignment.subtitle',
    icon: ARRIVAL_ICON.file,
    // `hubSeeInfo` in the prototype, and the hidden tinted row in the frame. The primary of the
    // placement path, exactly as `applyForJob` is the primary of the other two.
    recommended: true,
    destination: SCREEN.associateStatus,
  },
  {
    reason: ARRIVAL_REASON.applyForJob,
    titleKey: 'arrival.applyForJob.title',
    subtitleKey: 'arrival.applyForJob.subtitle',
    icon: ARRIVAL_ICON.search,
    // `hubSearchJob` in the prototype, and the visible tinted row in the frame.
    recommended: true,
    destination: SCREEN.jobs,
  },
  {
    reason: ARRIVAL_REASON.seeMyApplications,
    titleKey: 'arrival.seeMyApplications.title',
    subtitleKey: 'arrival.seeMyApplications.subtitle',
    icon: ARRIVAL_ICON.clock,
    // Same screen as `reconfirmAssignment`, as the prototype has it — both `data-to="active-status"`.
    destination: SCREEN.associateStatus,
  },
  {
    reason: ARRIVAL_REASON.somethingElse,
    titleKey: 'arrival.somethingElse.title',
    subtitleKey: 'arrival.somethingElse.subtitle',
    icon: ARRIVAL_ICON.bubble,
    destination: SCREEN.finish,
  },
  {
    reason: ARRIVAL_REASON.onboardingPaperwork,
    titleKey: 'arrival.onboardingPaperwork.title',
    subtitleKey: 'arrival.onboardingPaperwork.subtitle',
    icon: ARRIVAL_ICON.file,
    // KAP-6 shipped, so this is a real destination now rather than the marked approximation it was: the
    // I-9 step has its own screen and its own notice to the branch.
    destination: SCREEN.i9,
  },
  {
    /*
     * **Restored 2026-09-07. It was removed on 2026-09-02 against a decision approved the day before.**
     *
     * `ef61fb2` dropped this entry citing the live Figma frame, which draws five. But
     * [0127](07-decision-log) had already ruled SIX on 2026-09-01 — ISS reached consensus in the call that
     * the kiosk keeps handing out pay cards to returning associates (Heidi, 45:14) — and it says in as many
     * words that the app shipping six *"is already correct"* and *"was never drifting here"*. The frame is
     * what under-draws.
     *
     * So this is not a design departure to reconcile: it is a regression being undone. Where Figma and an
     * approved decision disagree about whether a capability EXISTS, the decision governs; Figma governs how
     * it looks.
     */
    reason: ARRIVAL_REASON.payCard,
    titleKey: 'arrival.payCard.title',
    subtitleKey: 'arrival.payCard.subtitle',
    icon: ARRIVAL_ICON.card,
    // The ASK first, never the scan screen [KPC-1a; 0077 §3; `R1 · Pay card — link a new one? (ask)`].
    // With a card on file it is E7's question instead — see `destinationOf`.
    destination: SCREEN.payCardAsk,
  },
  {
    reason: ARRIVAL_REASON.drugTest,
    titleKey: 'arrival.drugTest.title',
    subtitleKey: 'arrival.drugTest.subtitle',
    icon: ARRIVAL_ICON.flask,
    // `13 · Drug test info`, built 2026-09-11 [defect D1]. It landed on `finish` until then, which told a
    // candidate who had asked for a drug test to see a representative and offered them one *Start over*.
    destination: SCREEN.drugTest,
  },
];

/**
 * Where a tapped row goes. Its `destination`, except for the pay-card row of a candidate with a card on file.
 *
 * ⭐ **The pay-card row asks by the record** [Operator, 2026-10-16: «10a- A», to be recorded as decision
 * 0508]: a card on file lands on E7, *"Do you need a new pay card?"*; anything else lands on R1. `true` is
 * `usePayCardOnFile()`'s — the lookup said so, or a card was linked earlier in this visit [0470 § Derived].
 * `false` and `null` alike get R1: KPC-1f's fail-open — an unknown state takes the path that asks for a card.
 */
function destinationOf(option: ArrivalOption, payCardOnFile: boolean): Screen {
  return option.reason === ARRIVAL_REASON.payCard && payCardOnFile
    ? SCREEN.payCardNeed
    : option.destination;
}

/**
 * **A read that has not answered yet — which is NOT the same state as a read that failed.**
 *
 * The menu's read starts here. `unknown` is the fail-open value: *we asked and we did not learn*, and it
 * deliberately shows every option, because hiding a door on a network failure would tell a placed
 * associate their assignment is gone. `READING` is *we have not finished asking*, and it shows no options
 * at all — not because anything is hidden, but because the menu has not been entered yet.
 *
 * ⚠️ **Initialising on `unknown` made those two the same state, and that is defect D2**: the screen
 * rendered every row on the first paint and then deleted the ones the record forbids as the read
 * resolved, in front of the candidate. The Operator, 2026-09-11: *«primero se muestran todas las secciones
 * y luego desaparecen las que no se deben mostrar, esto tiene que pasar antes de entrar al menu no
 * despues»*. The fail-open is not what was wrong and it is untouched — a FAILED read still shows
 * everything, and there is a test for exactly that.
 *
 * A candidate with no id has no read to settle at all, which the gate below accounts for without a state
 * change.
 */
const READING = 'reading' as const;

/**
 * KID-5a — "What brings you in today?", the arrival-reason menu [Figma · «Returning user / 05 · What brings
 * you in today?» — Desktop · iPad · Mobile; it was «12 · What brings you in today?» until the page was
 * renamed]. It states nothing about the person in words: the heading, subheading and wait are the same for
 * every record, and only which rows render varies [0020; 0265's accepted residue].
 *
 * It sits **after identity is settled and before any job order**, and it is the one place in the product
 * where the person's own declaration is an input to routing. That does not contradict BR-K1 / decision 0001:
 * the door asks *who are you*, a claim about identity that the backend must verify because a wrong answer
 * creates a duplicate record; this asks *what did you come for*, a claim about intent that no lookup can
 * diagnose. Nothing here changes which record the person is, or what the kiosk reads or discloses.
 *
 * **Selecting routes immediately and never waits on the network.** The queue write is a staff surface: if it
 * fails, times out, or the admin side is unreachable, the candidate still reaches their destination and the
 * failure is raised to staff rather than to them [BR-K7; decision 0046]. Routing before the request also
 * makes the double-tap impossible — the screen is gone — which is how *"exactly one queue entry"* is kept
 * without a submitting flag.
 */
export function ArrivalReasonScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const payCardOnFile = usePayCardOnFile();
  /*
   * Back out of the menu leaves the visit, so it asks the Exit question rather than ending the session
   * itself [ISS-149] — the same overlay the top bar's `Exit` opens. Not `useBack`: on this screen `arrival`
   * may already be set by a previous tap, and its menu branch would send Back here, to itself.
   */
  const askToExit = useKioskSession((state) => state.askToExit);

  /*
   * **The menu composes from `{ held, i9 }` and nothing else** [KID-5a § Handover contract; decision 0387].
   *
   * ONE read, of the menu's own route. It replaced two — the status cards and the candidate profile — and
   * both carried the record (client name, street address, shift; fourteen profile fields) to a screen that
   * stands in a shared lobby. 0387 clause 1: the arrival screen never calls `GET /associate/:id/status`.
   * The api reduces both states before they cross the wire; the raw ISS compliance value never does.
   *
   * Absence is NOT a reason to hide anything. Every failure of this read — a timeout, a 5xx, a value nobody
   * has seen before — is `unknown`, which hides no row: hiding a door on a network failure would tell a
   * placed associate their assignment is gone [KID-5b; KID-5c].
   */
  const [menuState, setMenuState] = useState<typeof READING | ArrivalMenuState>(READING);
  useEffect(() => {
    // No id, no read — and nothing in flight is not a wait. The gate below reads that off `candidateId`
    // rather than writing a state here, which would be a cascading render for a fact already in hand.
    if (candidateId === undefined) return;
    let live = true;
    void fetchArrivalMenuState(candidateId)
      .then((answer) => {
        if (live) setMenuState(answer);
      })
      /*
       * **The menu WAITS on this promise, so it has to be impossible for it not to settle.** The service
       * says it never throws, and today it holds — but nothing enforces that, and this guard is the
       * difference between a menu that fails open and one that never arrives at all, which is the dead end
       * BR-K7 forbids. Same fail-open value as the service's own.
       */
      .catch((error: unknown) => {
        // Said out loud: the fail-open would otherwise hide that the service broke its own contract.
        console.error({
          msg: 'fetchArrivalMenuState threw — its never-throws contract is broken; the menu fails open',
          alert: 'arrival_menu_state_threw',
          name: error instanceof Error ? error.name : typeof error,
        });
        if (live) setMenuState(ARRIVAL_MENU_UNREAD);
      });
    return () => {
      live = false;
    };
  }, [candidateId]);

  /**
   * **The menu is not entered until the read has settled** [D2; 0175's fail-open, unchanged].
   *
   * The read decides which rows exist, so a list drawn before it answers is a list that has to be
   * corrected on screen. **A candidate the kiosk holds no id for is not waiting on anything** — there is
   * no read to make, so the menu renders on the first paint, fail-open, exactly as it always has. This is
   * the same shape the rest of the flow already uses for a wait — the card stays, its body holds the
   * spinner [`CheckingScreen`, `JobsScreen`, `AssociateStatusScreen`] — with one difference that is
   * deliberate: the heading and its subtitle stay mounted, so the screen does not arrive twice and
   * `useFocusOnMount` does not move focus a second time when the rows appear.
   */
  const reading = candidateId !== undefined && menuState === READING;
  const { held, i9 } = menuState === READING ? ARRIVAL_MENU_UNREAD : menuState;

  /*
   * ONE pass over the declared inventory — the conditional rows' predicates, and the placement-first rule
   * applied explicitly rather than inherited from `OPTIONS[0]` [KID-5b; 0198]. Both live in
   * `composeArrivalMenu`, which is where they are pinned against an inventory in a different order.
   */
  const options = composeArrivalMenu(OPTIONS, { held, i9 });

  // A screen abandoned mid-decision clears itself: back to Welcome, session gone, and no entry for a
  // selection that was never made [KID-5 § abandonment, KID-1's clear-on-idle rule one screen later].

  const selectArrival = useSelectArrival();
  const select = useCallback(
    (option: ArrivalOption) => {
      /*
       * Every selection is posted, and the post is held, never awaited — `useSelectArrival` carries the why. It
       * is shared with the status screen's *Yes* [03-us-kas-1a Scenario 11], so the two make the same act.
       */
      selectArrival(
        { reason: option.reason, queued: queuesForReason(option.reason) },
        destinationOf(option, payCardOnFile),
      );
    },
    [payCardOnFile, selectArrival],
  );

  return (
    <KioskScreen>
      <KioskCard
        header={
          <div className="flex w-full items-center justify-between">
            <BackLink onClick={askToExit} />
            <StatusBadge label={t('badge.kiosk')} />
          </div>
        }
      >
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('arrival.heading')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {t('arrival.subheading')}
            </p>
          </div>

          {reading ? (
            /*
             * The wait, in the body the rows will occupy. `aria-busy` with a polite live region so it is
             * announced once rather than being a silent pause for anyone not watching the ring — the same
             * block `JobsScreen` and `AssociateStatusScreen` render, and not `CheckingScreen` itself,
             * which hard-codes the lookup's own sentence.
             */
            <div
              aria-busy="true"
              aria-live="polite"
              className="flex w-full flex-col items-center gap-4 py-12"
            >
              <Spinner />
              <p className="text-sm leading-6 text-[var(--muted-foreground)]">
                {t('arrival.loading')}
              </p>
            </div>
          ) : (
            <ul className="flex w-full flex-col items-start gap-3">
              {options.map((option) => (
                <li className="w-full" key={option.reason}>
                  <ArrivalOptionButton
                    onClick={() => {
                      select(option);
                    }}
                    // The tint travels with the OPTION, never with its position in the filtered list — see
                    // `ArrivalOption.recommended` for the two sources that say so and the state where the
                    // position rule this replaced put the tint on a row neither of them draws tinted.
                    recommended={option.recommended ?? false}
                    icon={option.icon}
                    subtitle={t(option.subtitleKey)}
                    title={t(option.titleKey)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
